import { Component, OnInit, Renderer2, NgZone } from '@angular/core';
import { AppService } from '@services/app.service';
import {
  ApexAxisChartSeries,
  ApexNonAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexDataLabels,
  ApexGrid,
  ApexStroke,
  ApexFill,
  ApexLegend,
  ApexPlotOptions,
  ApexTooltip,
  ApexResponsive,
  ApexStates
} from 'ng-apexcharts';

export type ChartOptions = {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  chart: ApexChart;
  xaxis?: ApexXAxis;
  yaxis?: ApexYAxis | ApexYAxis[];
  dataLabels?: ApexDataLabels;
  grid?: ApexGrid;
  stroke?: ApexStroke;
  colors?: string[];
  fill?: ApexFill;
  legend?: ApexLegend;
  plotOptions?: ApexPlotOptions;
  labels?: string[];
  tooltip?: ApexTooltip;
  responsive?: ApexResponsive[];
  states?: ApexStates;
};

interface StatCard {
  icon?: string;
  iconClass?: string;
  value: string;
  unit: string;
  label: string;
}

interface TicketStats {
  total: number;
  inProgress: number;
  onHold: number;
  resolved: number;
  closed: number;
}

interface FsrStats {
  total: number;
  inProgress: number;
  onHold: number;
  resolved: number;
  approved: number;
  closed: number;
}

interface BenefitRow {
  icon: string;
  iconBg: string;
  value: string;
  unit: string;
  label: string;
  reverse?: boolean;
}

/**
 * Each dashboard section loads independently: it shows a skeleton until its
 * own API answers, then either its data or an "unavailable" notice — one slow
 * or failing endpoint never holds up (or fakes data for) the others.
 */
type LoadState = 'loading' | 'ready' | 'error';
type DashboardSection = 'summary' | 'weather' | 'yield' | 'tickets' | 'fsr' | 'plants' | 'alarms' | 'benefits';

@Component({
  selector: 'app-dashboard-revamp',
  templateUrl: './dashboard-revamp.component.html',
  styleUrls: ['./dashboard-revamp.component.scss']
})
export class DashboardRevampComponent implements OnInit {

  today: Date = new Date();

  viewMode: 'chart' | 'world' = 'chart';

  sectionState: Record<DashboardSection, LoadState> = {
    summary: 'loading', weather: 'loading', yield: 'loading', tickets: 'loading',
    fsr: 'loading', plants: 'loading', alarms: 'loading', benefits: 'loading'
  };

  /** State of whichever dataset (tickets / FSR) the pill toggle is showing. */
  get statsState(): LoadState {
    return this.sectionState[this.statsView];
  }

  // Yield Statistics gets its own loader since it re-fetches on every
  // Year/Lifetime toggle or year shift, independent of the initial page load.
  yieldLoading = false;

  // null until /dashboard/weather/ answers — no invented placeholder reading.
  weather: { temp: number; condition: string; icon: string } | null = null;

  statCards: StatCard[] = [
    { icon: 'assets/img/icons/ic_thunder.svg', value: '0.00', unit: 'MW', label: 'Current power' },
    { icon: 'assets/img/icons/ic_sola-plate.svg', value: '0.00', unit: 'GWh', label: 'Total yield' },
    { icon: 'assets/img/icons/ic_sun.svg', value: '0.00', unit: 'MWh', label: 'Yield today' },
    { icon: 'assets/img/icons/ic_battery.svg', value: '0.00', unit: 'MWp', label: 'Total Capacity' }
  ];

  yieldRange: 'year' | 'lifetime' = 'year';
  selectedYear = 2026;

  // Populated from /dashboard/yield-statistics/ (see fetchYieldStatistics).
  private yieldSeriesData: number[] = [];
  private yieldCategoriesData: string[] = [];

  ticketStats: TicketStats = { total: 0, inProgress: 0, onHold: 0, resolved: 0, closed: 0 };
  fsrStats: FsrStats = { total: 0, inProgress: 0, onHold: 0, resolved: 0, approved: 0, closed: 0 };
  /** Which data the "Tickets" panel currently shows — switched by its pill toggle. */
  statsView: 'tickets' | 'fsr' = 'tickets';

  // Categories the user has clicked off in each donut's legend (see
  // togglePlantCategory/toggleAlarmCategory).
  private plantHidden = new Set<string>();
  private alarmHidden = new Set<string>();

  plantStatus = { faulty: 0, normal: 0, offline: 0 };

  alarmStatus = { critical: 0, major: 0, minor: 0, warning: 0 };

  environmentalBenefits: BenefitRow[] = [
    { icon: 'assets/img/icons/ic_coal.svg', iconBg: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, #A3EAFF 100%)', value: '--', unit: '(tons)', label: 'Standard coal saved' },
    { icon: 'assets/img/icons/ic_plant.svg', iconBg: 'linear-gradient(90deg, rgba(255, 204, 8, 0.20) 0%, rgba(255, 255, 255, 0.00) 100%)', value: '--', unit: '(tons)', label: 'CO₂ avoided', reverse: true },
    { icon: 'assets/img/icons/ic_leaf.svg', iconBg: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, #9AEBAC 100%)', value: '--', unit: '(tons)', label: 'Equivalent trees planted' }
  ];

  yieldChart: Partial<ChartOptions> = { series: [] };
  ticketsChart: Partial<ChartOptions> = { series: [] };
  fsrStatsChart: Partial<ChartOptions> = { series: [] };
  plantStatusChart: Partial<ChartOptions> = { series: [] };
  alarmsChart: Partial<ChartOptions> = { series: [] };

  get totalPlants(): number {
    return this.plantStatus.faulty + this.plantStatus.normal + this.plantStatus.offline;
  }

  get totalAlarms(): number {
    return this.alarmStatus.critical + this.alarmStatus.major + this.alarmStatus.minor + this.alarmStatus.warning;
  }

  constructor(private renderer: Renderer2, private appService: AppService, private ngZone: NgZone) {
    this.buildYieldChart();
    this.buildTicketsChart();
    this.buildFsrStatsChart();
    this.buildPlantStatusChart();
    this.buildAlarmsChart();
  }

  ngOnInit(): void {
    this.renderer.addClass(document.body, 'revamp-bg');
    this.fetchDashboardSummary();
    this.fetchYieldStatistics();
    this.fetchEnvironmentalBenefits();
    this.fetchWeather();
    this.fetchPlantStatus();
    this.fetchAlarmStatus();
    this.fetchTicketStats();
    this.fetchFsrStats();
  }

  private fetchDashboardSummary(): void {
    this.appService.GetDashboardSummary().subscribe({
      next: (res) => {
        const data = res?.data?.[0];
        if (!data) {
          this.sectionState.summary = 'error';
          return;
        }
        this.statCards[0].value = ((data.current_power_kw ?? 0) / 1_000).toFixed(2);
        this.statCards[1].value = ((data.total_yield ?? 0) / 1_000_000).toFixed(2);
        this.statCards[2].value = ((data.yield_today ?? 0) / 1_000).toFixed(2);
        this.statCards[3].value = ((data.total_capacity_kw ?? 0) / 1_000).toFixed(2);
        this.sectionState.summary = 'ready';
      },
      error: (err) => {
        console.log('/dashboard/summary/ error:', err?.error?.message);
        this.sectionState.summary = 'error';
      }
    });
  }

  private fetchYieldStatistics(): void {
    // First load (or a retry after a failure) shows the skeleton; later
    // Year/Lifetime/year-shift refetches keep the old chart under a spinner.
    if (this.sectionState.yield !== 'ready') {
      this.sectionState.yield = 'loading';
    }
    this.yieldLoading = true;
    const type = this.yieldRange === 'year' ? 'yearly' : 'lifetime';
    this.appService.GetYieldStatistics(type, this.selectedYear).subscribe({
      next: (res) => {
        const rows = res?.data as Array<{ period: string; energy_kwh: number }> | undefined;
        this.yieldLoading = false;
        if (!rows) {
          this.sectionState.yield = 'error';
          return;
        }
        // "2026-01" (yearly, per-month) -> "01"; a lifetime period like
        // "2026" (per-year) is shown as-is.
        this.yieldCategoriesData = rows.map(r => {
          const parts = (r.period ?? '').split('-');
          return parts.length > 1 ? parts[1] : (r.period ?? '');
        });
        this.yieldSeriesData = rows.map(r => this.safeNumber(r.energy_kwh));
        this.buildYieldChart();
        this.sectionState.yield = 'ready';
      },
      error: (err) => {
        console.log('/dashboard/yield-statistics/ error:', err?.error?.message);
        this.yieldLoading = false;
        this.sectionState.yield = 'error';
      }
    });
  }

  private fetchEnvironmentalBenefits(): void {
    this.appService.GetEnvironmentalBenefits().subscribe({
      next: (res) => {
        const data = res?.data?.[0];
        if (!data) {
          this.sectionState.benefits = 'error';
          return;
        }
        this.environmentalBenefits[0].value = this.abbreviateNumber(data.reduction_coal ?? 0);
        this.environmentalBenefits[1].value = this.abbreviateNumber(data.reduction_co2 ?? 0);
        this.environmentalBenefits[2].value = this.abbreviateNumber(data.reduction_tree ?? 0);
        this.sectionState.benefits = 'ready';
      },
      error: (err) => {
        console.log('/dashboard/environmental-benefits/ error:', err?.error?.message);
        this.sectionState.benefits = 'error';
      }
    });
  }

  private fetchWeather(): void {
    this.appService.GetDashboardWeather().subscribe({
      next: (res) => {
        const forecast = res?.data?.[0]?.forecast as Array<{ day: string; temp?: number; min_temp: number; max_temp: number; weather: string }> | undefined;
        if (!forecast?.length) {
          this.sectionState.weather = 'error';
          return;
        }
        const todayName = this.today.toLocaleDateString('en-US', { weekday: 'long' });
        const entry = forecast.find(f => f.day === todayName) ?? forecast[0];
        this.weather = {
          // Prefer the API's current temp when it sends one; else the day's mid-point.
          temp: entry.temp != null ? Math.round(entry.temp) : Math.round(((entry.min_temp ?? 0) + (entry.max_temp ?? 0)) / 2),
          condition: entry.weather ?? '',
          icon: this.weatherIcon(entry.weather ?? '')
        };
        this.sectionState.weather = 'ready';
      },
      error: (err) => {
        console.log('/dashboard/weather/ error:', err?.error?.message);
        this.sectionState.weather = 'error';
      }
    });
  }

  /**
   * Picks the weather-card icon (src/assets/img/weather) from the free-text
   * condition the API returns ("Light rain", "Partly cloudy", ...). More
   * specific phrases are tested first; night variants are used after dusk
   * when one exists. An unrecognised condition falls back to the neutral
   * cloud rather than guessing sun or rain.
   */
  private weatherIcon(condition: string): string {
    const c = condition.toLowerCase();
    const hour = new Date().getHours();
    const night = hour < 6 || hour >= 18;
    const has = (...words: string[]) => words.some(w => c.includes(w));
    const pick = (day: string, nightIcon?: string) => `assets/img/weather/${night && nightIcon ? nightIcon : day}.svg`;

    if (has('thunder', 'storm', 'lightning')) {
      return has('rain', 'shower') ? pick('thunderstorm', 'thunderstorm-night')
        : has('sun', 'partly') ? pick('thunder-sun', 'thunder-sun-night')
        : pick('thunder', 'thunder-night');
    }
    if (has('sleet', 'rain and snow', 'wintry')) {
      return pick('sleet', 'sleet-night');
    }
    if (has('snow', 'flurr', 'blizzard', 'ice', 'hail')) {
      return has('wind', 'blizzard') ? 'assets/img/weather/wind-snow.svg'
        : has('sun') ? 'assets/img/weather/sun-snow.svg'
        : has('flurr') ? pick('flurries', 'snow-light-night')
        : has('light') ? pick('snow-light', 'snow-light-night')
        : pick('snow', 'snow-night');
    }
    if (has('rain', 'shower', 'drizzle')) {
      return has('heavy', 'torrential', 'extreme') ? pick('rain-heavy', 'rain-heavy-night')
        : has('drizzle') ? pick('shower-day', 'drizzle-night')
        : has('shower') ? pick('shower-day', 'shower-night')
        : has('light', 'slight') ? pick('rain-light', 'rain-light-night')
        : pick('rain', 'rain-night');
    }
    if (has('fog', 'mist')) {
      return pick('fog', 'fog-night');
    }
    if (has('haze', 'smoke', 'dust', 'sand')) {
      return 'assets/img/weather/haze.svg';
    }
    if (has('wind', 'breez', 'gust')) {
      return has('sun', 'clear', 'fair') ? 'assets/img/weather/wind-sun.svg' : 'assets/img/weather/wind.svg';
    }
    if (has('partly', 'mostly sunny', 'mostly clear', 'few clouds', 'scattered', 'broken')) {
      return pick('partly-cloudy-day', 'partly-cloudy-night');
    }
    if (has('overcast')) {
      return 'assets/img/weather/overcast.svg';
    }
    if (has('clear', 'sun', 'fair')) {
      return pick('clear-day', 'clear-night');
    }
    return pick('cloudy', 'cloudy-night');
  }

  private fetchPlantStatus(): void {
    this.appService.GetPlantStatus().subscribe({
      next: (res) => {
        const data = res?.data?.[0];
        if (!data) {
          this.sectionState.plants = 'error';
          return;
        }
        this.plantStatus = { faulty: data.faulty ?? 0, normal: data.normal ?? 0, offline: data.offline ?? 0 };
        this.buildPlantStatusChart();
        this.sectionState.plants = 'ready';
      },
      error: (err) => {
        console.log('getPlantsStatus error:', err?.error?.message);
        this.sectionState.plants = 'error';
      }
    });
  }

  private fetchAlarmStatus(): void {
    this.appService.GetAlarmStatus().subscribe({
      next: (res) => {
        const alarms = res?.data?.[0]?.alarm_status as Array<{ alarm_type: string; count: number }> | undefined;
        if (!alarms) {
          this.sectionState.alarms = 'error';
          return;
        }
        const next = { critical: 0, major: 0, minor: 0, warning: 0 };
        alarms.forEach((item) => {
          const key = item.alarm_type?.toLowerCase() as keyof typeof next;
          if (key in next) {
            next[key] = item.count ?? 0;
          }
        });
        this.alarmStatus = next;
        this.buildAlarmsChart();
        this.sectionState.alarms = 'ready';
      },
      error: (err) => {
        console.log('getAlarmsAndStatusCount error:', err?.error?.message);
        this.sectionState.alarms = 'error';
      }
    });
  }

  private fetchTicketStats(): void {
    this.appService.GetTicketStats().subscribe({
      next: (res) => {
        const data = res?.data?.[0];
        if (!data) {
          this.sectionState.tickets = 'error';
          return;
        }
        this.ticketStats = {
          total: data.total_tickets ?? 0,
          inProgress: data.in_progress ?? 0,
          onHold: data.on_hold_pending_material ?? 0,
          resolved: data.resolved ?? 0,
          closed: data.closed ?? 0
        };
        this.buildTicketsChart();
        this.sectionState.tickets = 'ready';
      },
      error: (err) => {
        console.log('/dashboard/ticket-stats/ error:', err?.error?.message);
        this.sectionState.tickets = 'error';
      }
    });
  }

  /** FSR counterpart of fetchTicketStats — same shape, one extra Approved bucket. */
  private fetchFsrStats(): void {
    this.appService.GetFsrStats().subscribe({
      next: (res) => {
        const data = res?.data?.[0];
        if (!data) {
          this.sectionState.fsr = 'error';
          return;
        }
        this.fsrStats = {
          total: data.total_fsr ?? 0,
          inProgress: data.in_progress ?? 0,
          onHold: data.on_hold_pending_material ?? 0,
          resolved: data.resolved ?? 0,
          approved: data.approved ?? 0,
          closed: data.closed ?? 0
        };
        this.buildFsrStatsChart();
        this.sectionState.fsr = 'ready';
      },
      error: (err) => {
        console.log('/dashboard/fsr-stats/ error:', err?.error?.message);
        this.sectionState.fsr = 'error';
      }
    });
  }

  // Chart formatters must never render "[object Object]" if ApexCharts ever
  // hands them something other than a plain number (seen intermittently
  // with this ApexCharts version's tooltip/dataLabel callbacks).
  private safeNumber(val: unknown): number {
    const num = typeof val === 'number' ? val : Number(val);
    return Number.isFinite(num) ? num : 0;
  }

  // Matches the abbreviation convention already used for PKR amounts
  // elsewhere in the app (plant-detail.component.ts's convertRoiAmount);
  // reused here for environmental-benefit values too since both need the
  // same "pick the right magnitude" K/M/B formatting.
  private abbreviateNumber(value: number): string {
    if (value >= 1_000_000_000) {
      return (value / 1_000_000_000).toFixed(2) + 'B';
    }
    if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(2) + 'M';
    }
    if (value >= 1_000) {
      return (value / 1_000).toFixed(2) + 'K';
    }
    return value.toFixed(2);
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'revamp-bg');
  }

  retryYield(): void {
    this.fetchYieldStatistics();
  }

  setYieldRange(range: 'year' | 'lifetime'): void {
    if (this.yieldRange === range) {
      return;
    }
    this.yieldRange = range;
    this.fetchYieldStatistics();
  }

  shiftYear(step: number): void {
    this.selectedYear += step;
    if (this.yieldRange === 'year') {
      this.fetchYieldStatistics();
    }
  }

  setViewMode(mode: 'chart' | 'world'): void {
    this.viewMode = mode;
  }

  /** Both stats are already fetched on init, so switching is instant — no refetch. */
  setStatsView(view: 'tickets' | 'fsr'): void {
    this.statsView = view;
  }

  private buildYieldChart(): void {
    this.yieldChart = {
      series: [{ name: 'Yield', data: this.yieldSeriesData }],
      chart: {
        type: 'bar',
        height: 215,
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: 'inherit'
        // Bar hover tooltip disabled for now — re-enable together with the
        // `tooltip:` line below:
        // , events: this.barTooltipEvents
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '8px'
        }
      },
      dataLabels: { enabled: false },
      colors: ['#78D18A'],
      grid: {
        strokeDashArray: 4,
        borderColor: '#EEF1F4',
        yaxis: { lines: { show: true } }
      },
      xaxis: {
        categories: this.yieldCategoriesData,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: '#8A94A6', fontSize: '12px' } }
      },
      yaxis: {
        labels: {
          style: { colors: '#8A94A6', fontSize: '12px' },
          formatter: (val: number) => this.safeNumber(val).toLocaleString()
        }
      },
      tooltip: { enabled: false },
      // Hover tooltip disabled for now:
      // tooltip: this.barTooltip(label => {
      //   // Yearly view categories are month numbers ("01".."12"); lifetime
      //   // categories are already years.
      //   const month = this.yieldRange === 'year' && /^\d{1,2}$/.test(label) ? +label : 0;
      //   return month >= 1 && month <= 12
      //     ? new Date(2000, month - 1, 1).toLocaleString('en-US', { month: 'short' }) + ' ' + this.selectedYear
      //     : label;
      // }, ' kWh'),
      // ApexCharts' own default bar hover effect is a plain darken filter,
      // which fights with the CSS "spark" glow animation on
      // .apexcharts-bar-area (dashboard-revamp.component.scss) — turning it
      // off here lets the glow be the only thing that runs on hover.
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } }
      }
    };
  }

  private buildTicketsChart(): void {
    const rows = [
      { name: 'Total', value: this.ticketStats.total },
      { name: 'In Progress', value: this.ticketStats.inProgress },
      { name: 'On Hold', value: this.ticketStats.onHold },
      { name: 'Resolved', value: this.ticketStats.resolved },
      { name: 'Closed', value: this.ticketStats.closed }
    ];
    this.ticketsChart = {
      series: [{ name: 'Tickets', data: rows.map(r => r.value) }],
      chart: {
        type: 'bar',
        height: 215,
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: 'inherit'
        // Bar hover tooltip disabled for now — re-enable together with the
        // `tooltip:` line below:
        // , events: this.barTooltipEvents
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '10px',
          dataLabels: { position: 'top' }
        }
      },
      dataLabels: {
        enabled: true,
        textAnchor: 'start',
        offsetX: 20,
        style: { colors: ['#1F2937'], fontSize: '12px', fontWeight: 600 },
        formatter: (val: number) => this.safeNumber(val).toLocaleString()
      },
      colors: ['#00B3FF'],
      grid: {
        strokeDashArray: 4,
        borderColor: '#EEF1F4',
        xaxis: { lines: { show: true } },
        padding: { right: 60 }
      },
      xaxis: {
        categories: rows.map(r => r.name),
        // Headroom past the longest bar so its value label has room beside
        // the bar instead of being pushed back over it.
        max: Math.max(4, Math.ceil(Math.max(...rows.map(r => r.value)) * 1.15)),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: '#8A94A6', fontSize: '12px' } }
      },
      yaxis: {
        labels: { style: { colors: '#4B5563', fontSize: '13px' } }
      },
      tooltip: { enabled: false },
      // Hover tooltip disabled for now:
      // tooltip: this.barTooltip(label => label),
      // See note in buildYieldChart.
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } }
      }
    };
  }

  /** FSR counterpart of buildTicketsChart — identical shape, one extra Approved bar. */
  private buildFsrStatsChart(): void {
    const rows = [
      { name: 'Total', value: this.fsrStats.total },
      { name: 'In Progress', value: this.fsrStats.inProgress },
      { name: 'On Hold', value: this.fsrStats.onHold },
      { name: 'Resolved', value: this.fsrStats.resolved },
      { name: 'Approved', value: this.fsrStats.approved },
      { name: 'Closed', value: this.fsrStats.closed }
    ];
    this.fsrStatsChart = {
      series: [{ name: 'FSR', data: rows.map(r => r.value) }],
      chart: {
        type: 'bar',
        height: 215,
        toolbar: { show: false },
        animations: { enabled: false },
        fontFamily: 'inherit'
        // Bar hover tooltip disabled for now — re-enable together with the
        // `tooltip:` line below:
        // , events: this.barTooltipEvents
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: '10px',
          dataLabels: { position: 'top' }
        }
      },
      dataLabels: {
        enabled: true,
        textAnchor: 'start',
        offsetX: 20,
        style: { colors: ['#1F2937'], fontSize: '12px', fontWeight: 600 },
        formatter: (val: number) => this.safeNumber(val).toLocaleString()
      },
      colors: ['#00B3FF'],
      grid: {
        strokeDashArray: 4,
        borderColor: '#EEF1F4',
        xaxis: { lines: { show: true } },
        padding: { right: 60 }
      },
      xaxis: {
        categories: rows.map(r => r.name),
        // Headroom past the longest bar so its value label has room beside
        // the bar instead of being pushed back over it.
        max: Math.max(4, Math.ceil(Math.max(...rows.map(r => r.value)) * 1.15)),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: '#8A94A6', fontSize: '12px' } }
      },
      yaxis: {
        labels: { style: { colors: '#4B5563', fontSize: '13px' } }
      },
      tooltip: { enabled: false },
      // Hover tooltip disabled for now:
      // tooltip: this.barTooltip(label => label),
      // See note in buildYieldChart.
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } }
      }
    };
  }

  private interleaveWithGaps(
    values: number[],
    colors: string[],
    labels: string[],
    keys: string[],
    hidden: Set<string>,
    trailingGap = true
  ): { series: number[]; colors: string[]; labels: string[]; active: { key: string; value: number; color: string; label: string; hidden: boolean; empty: boolean }[] } {
    // Zero-count categories keep their legend row (so the legend always lists
    // every category in the agreed order, e.g. "0 Critical") but get no arc:
    // a zero-length arc with the round-linecap stroke renders as a stray
    // filled dot, so they're drawn as the same transparent sliver a
    // user-hidden category uses.
    const active = values.map((v, i) => ({
      key: keys[i], value: v, color: colors[i], label: labels[i],
      hidden: hidden.has(keys[i]), empty: !(v > 0)
    }));

    const visibleTotal = active.reduce((sum, e) => sum + (e.hidden || e.empty ? 0 : e.value), 0);
    const gap = Math.max(visibleTotal * 0.04, 0.5);
    const series: number[] = [];
    const gappedColors: string[] = [];
    const gappedLabels: string[] = [];
    active.forEach(entry => {
      const noArc = entry.hidden || entry.empty;
      series.push(noArc ? gap : entry.value, gap);
      gappedColors.push(noArc ? 'transparent' : entry.color, 'transparent');
      gappedLabels.push(entry.label, '');
    });
    // A full ring needs a spacer after the last slice (it meets the first
    // one again). A half-donut doesn't: the trailing spacer would eat the
    // last few degrees and leave the right end of the arc slanted instead of
    // sitting flat on the base line like the left end.
    if (!trailingGap) {
      series.pop();
      gappedColors.pop();
      gappedLabels.pop();
    }
    return { series, colors: gappedColors, labels: gappedLabels, active };
  }

  // Per-slice legend styling (marker/text color, display text) and the
  // series-index -> category-key map, all padded to the full gapped
  // (interleaved) length — one entry per slice, gap slots included — so a
  // legend row's positional index lines up with its real series index. A
  // shorter, gap-free array (one entry per *real* category) made legend row
  // N resolve to series[N] instead of series[2N], landing on a transparent
  // gap filler slice instead of the real one — which is what was showing a
  // stray value like "0.7000000000000001" in the donut center on hover.
  //
  // Text is supplied via legend.formatter rather than customLegendItems:
  // ApexCharts' own legend click/hover handling (onLegendClick,
  // onLegendHovered) is unconditionally skipped whenever
  // legend.customLegendItems is non-empty — confirmed in
  // node_modules/apexcharts/dist/apexcharts.common.js, which gates that
  // whole handler behind `!e.config.legend.customLegendItems.length`. That
  // was silently no-op'ing our click-to-toggle no matter how it was wired.
  // legend.formatter has no such restriction.
  private buildGappedLegend(gapped: { labels: string[]; active: { key: string; value: number; color: string; label: string; hidden: boolean }[] }): { itemTexts: string[]; fillColors: string[]; textColors: string[]; indexToKey: (string | null)[] } {
    const itemTexts: string[] = [];
    const fillColors: string[] = [];
    const textColors: string[] = [];
    const indexToKey: (string | null)[] = [];
    let activeIdx = 0;
    gapped.labels.forEach(label => {
      if (label) {
        const entry = gapped.active[activeIdx++];
        itemTexts.push(`${entry.value} ${entry.label}`);
        fillColors.push(entry.hidden ? '#D5D9E0' : entry.color);
        textColors.push(entry.hidden ? '#AEB4BD' : '#2D2D2D');
        indexToKey.push(entry.key);
      } else {
        itemTexts.push('');
        fillColors.push('transparent');
        textColors.push('#2D2D2D');
        indexToKey.push(null);
      }
    });
    return { itemTexts, fillColors, textColors, indexToKey };
  }

  // ApexCharts' own legend click-to-toggle (legend.onItemClick.toggleDataSeries)
  // only does anything for axis charts (bar/line/etc.) and radialBar — the
  // library's internal handler explicitly skips pie/donut chart types, so it
  // silently no-ops here no matter how it's configured. Hiding/showing a
  // slice is implemented by hand instead, via the chart.events.legendClick
  // callback below, which fires for every chart type.
  private togglePlantCategory(key: string): void {
    if (this.plantHidden.has(key)) {
      this.plantHidden.delete(key);
    } else {
      this.plantHidden.add(key);
    }
    this.buildPlantStatusChart();
  }

  private toggleAlarmCategory(key: string): void {
    if (this.alarmHidden.has(key)) {
      this.alarmHidden.delete(key);
    } else {
      this.alarmHidden.add(key);
    }
    this.buildAlarmsChart();
  }

  // ApexCharts parks a bar tooltip to the right of the bar and flips it to
  // the left once the bar is past the chart's midpoint, with its edge at the
  // bar — so hovering the 2024 column puts the tip over 2023. Re-centre it
  // above the pointer instead.
  //
  // ApexCharts also re-positions the tip ~100ms after each hover by writing
  // inline left/top, which would undo any inline position set here. So the
  // wanted position goes in CSS variables it doesn't touch, and the
  // .db-tip--centered rule in the stylesheet applies them with !important
  // (which beats an inline style).
  private barTooltipEvents = {
    mouseMove: (e: MouseEvent, chart: any) => {
      const place = () => {
        const tip = chart?.el?.querySelector('.apexcharts-tooltip') as HTMLElement | null;
        const box = tip?.offsetParent as HTMLElement | null;
        if (!tip || !box) {
          return;
        }
        const area = box.getBoundingClientRect();
        const left = e.clientX - area.left - tip.offsetWidth / 2;
        tip.style.setProperty('--tip-left', Math.max(0, Math.min(left, area.width - tip.offsetWidth)) + 'px');
        tip.style.setProperty('--tip-top', Math.max(0, e.clientY - area.top - tip.offsetHeight - 12) + 'px');
        tip.classList.add('db-tip--centered');
      };
      place();
      // Again once ApexCharts has swapped in the hovered bar's content, since
      // the tip's width (and so the centring) depends on it.
      setTimeout(place, 120);
    }
  };

  // Hover tooltip for the bar charts, in the same dark pill as the donuts:
  // "<category> <value>". `describe` turns the raw category label into what
  // should be shown (e.g. "03" -> "Mar 2026" for the yearly yield chart).
  private barTooltip(describe: (label: string) => string, unit = ''): ApexTooltip {
    return {
      enabled: true,
      custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
        const raw = w?.globals?.labels?.[dataPointIndex] ?? w?.config?.xaxis?.categories?.[dataPointIndex] ?? '';
        const value = this.safeNumber(series?.[seriesIndex]?.[dataPointIndex]).toLocaleString();
        return `<div class="db-chart-tip"><strong>${describe(String(raw))}</strong> ${value}${unit}</div>`;
      }
    };
  }

  // "Normal 410 (71.43%)"-style hover tooltip for the donuts. Only real
  // slices can be hovered (spacer slices are pointer-events:none), so the
  // series index always maps back to a category through indexToKey.
  private donutTooltip(
    gapped: { active: { key: string; value: number; label: string }[] },
    indexToKey: (string | null)[]
  ): ApexTooltip {
    const total = gapped.active.reduce((sum, e) => sum + e.value, 0);
    return {
      enabled: true,
      custom: ({ seriesIndex }: { seriesIndex: number }) => {
        const entry = gapped.active.find(e => e.key === indexToKey[seriesIndex]);
        if (!entry) {
          return '';
        }
        const pct = total > 0 ? ((entry.value / total) * 100).toFixed(2) : '0.00';
        return `<div class="db-chart-tip"><strong>${entry.label} ${entry.value}</strong> (${pct}%)</div>`;
      }
    };
  }

  private buildPlantStatusChart(): void {
    const { faulty, normal, offline } = this.plantStatus;
    const gapped = this.interleaveWithGaps(
      [faulty, normal, offline],
      ['#FF6C68', '#AD89EA', '#B0BEC5'],
      ['Faulty', 'Normal', 'Offline'],
      ['faulty', 'normal', 'offline'],
      this.plantHidden,
      false
    );
    const { itemTexts: legendItemTexts, fillColors: legendFillColors, textColors: legendTextColors, indexToKey } = this.buildGappedLegend(gapped);
    // The transparent gap slices still need their own stroke color — a
    // "white" stroke applies to every slice regardless of fill, so without
    // this each gap renders as a solid white blob (the "garbage" slices).
    const strokeColors = [...gapped.colors];
    this.plantStatusChart = {
      series: gapped.series,
      labels: gapped.labels,
      chart: {
        type: 'donut',
        height: 360,
        animations: { enabled: false },
        fontFamily: 'inherit',
        events: {
          // See note above togglePlantCategory: ApexCharts' own
          // legend.onItemClick.toggleDataSeries is a no-op for donut/pie
          // charts, so hide/show is implemented here by hand. ng-apexcharts
          // constructs/renders the chart via ngZone.runOutsideAngular, so
          // ApexCharts' own DOM click listener fires this callback outside
          // Angular's zone too — without ngZone.run, reassigning
          // plantStatusChart here would update the component but never
          // trigger change detection, so the template would just never
          // re-render it.
          legendClick: (_ctx: unknown, seriesIndex?: number) => {
            const key = seriesIndex != null ? indexToKey[seriesIndex] : null;
            if (key) {
              this.ngZone.run(() => this.togglePlantCategory(key));
            }
          }
        }
      } as any,
      colors: gapped.colors,
      stroke: { show: true, width: 6, colors: strokeColors, lineCap: 'round' } as any,
      fill: { opacity: 1 },
      dataLabels: { enabled: false },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '13px',
        fontFamily: 'inherit',
        fontWeight: 400,
        labels: { colors: legendTextColors } as any,
        markers: { width: 12, height: 12, radius: 12, fillColors: legendFillColors } as any,
        itemMargin: { horizontal: 10, vertical: 4 },
        formatter: ((_name: string, opts: any) => legendItemTexts[opts?.seriesIndex] ?? '') as any
      },
      plotOptions: {
        pie: {
          offsetY: 55,
          startAngle: -90,
          endAngle: 90,
          donut: {
            size: '68%',
            labels: {
              show: true,
              // The half-donut's centre is its flat base, so default label
              // placement straddles that line; lift both lines above it.
              name: { show: true, offsetY: -30 },
              value: {
                show: true,
                fontSize: '26px',
                fontWeight: 700,
                color: '#1F2937',
                offsetY: -10,
                formatter: (val: unknown) => this.safeNumber(val).toLocaleString(undefined, { maximumFractionDigits: 0 })
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Total Plants',
                fontSize: '12px',
                fontWeight: 500,
                color: '#8A94A6',
                formatter: () => this.totalPlants.toString()
              }
            }
          }
        }
      },
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } }
      },
      tooltip: this.donutTooltip(gapped, indexToKey)
    };
  }

  private buildAlarmsChart(): void {
    const { critical, major, minor, warning } = this.alarmStatus;
    const gapped = this.interleaveWithGaps(
      [critical, major, minor, warning],
      ['#FF6C68', '#FFB659', '#EA99FF', '#6CBAFF'],
      ['Critical', 'Major', 'Minor', 'Warning'],
      ['critical', 'major', 'minor', 'warning'],
      this.alarmHidden
    );
    const { itemTexts: legendItemTexts, fillColors: legendFillColors, textColors: legendTextColors, indexToKey } = this.buildGappedLegend(gapped);
    // See note in buildPlantStatusChart: gap slices need their own
    // (transparent) stroke color or they render as solid white blobs.
    const strokeColors = [...gapped.colors];
    this.alarmsChart = {
      series: gapped.series,
      labels: gapped.labels,
      chart: {
        type: 'donut',
        height: 265,
        animations: { enabled: false },
        fontFamily: 'inherit',
        events: {
          // See note in buildPlantStatusChart.
          legendClick: (_ctx: unknown, seriesIndex?: number) => {
            const key = seriesIndex != null ? indexToKey[seriesIndex] : null;
            if (key) {
              this.ngZone.run(() => this.toggleAlarmCategory(key));
            }
          }
        }
      } as any,
      colors: gapped.colors,
      stroke: { show: true, width: 6, colors: strokeColors, lineCap: 'round' } as any,
      fill: { opacity: 1 },
      dataLabels: { enabled: false },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '13px',
        fontFamily: 'inherit',
        fontWeight: 400,
        labels: { colors: legendTextColors } as any,
        markers: { width: 12, height: 12, radius: 12, fillColors: legendFillColors } as any,
        itemMargin: { horizontal: 10, vertical: 4 },
        formatter: ((_name: string, opts: any) => legendItemTexts[opts?.seriesIndex] ?? '') as any
      },
      plotOptions: {
        pie: {
          offsetY: 20,
          donut: {
            size: '62%',
            labels: {
              show: true,
              value: {
                show: true,
                fontSize: '26px',
                fontWeight: 700,
                color: '#1F2937',
                offsetY: 4,
                formatter: (val: unknown) => this.safeNumber(val).toLocaleString(undefined, { maximumFractionDigits: 0 })
              },
              total: {
                show: true,
                showAlways: true,
                label: 'Total Alarms',
                fontSize: '12px',
                fontWeight: 500,
                color: '#8A94A6',
                formatter: () => this.totalAlarms.toString()
              }
            }
          }
        }
      },
      states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } }
      },
      tooltip: this.donutTooltip(gapped, indexToKey)
    };
  }
}
